import { useSpaceParameterOpenClause } from '@src/components/AppLayout/SpaceParameters';
import {
  ACTION,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  BlotterTableOverlay,
  Button,
  ButtonVariants,
  CUSTOMER_BALANCE,
  CUSTOM_BLOTTER_GROUPING,
  CollapseAllButton,
  CustomerLedgerUpdateTypeEnum,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  Divider,
  ExpandAllButton,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  Toggle,
  agGridMenuSeparator,
  columnTypes,
  filterByCellValueMenuItem,
  isGridApiReady,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useAggDeltaUpdatesPipe,
  useBlotterGroupingToggling,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useJsonModal,
  useMixpanel,
  usePersistedBlotterTable,
  useWSFilterPipe,
  useWsBlotterTable,
  type BlotterGroupingDefinition,
  type CustomerBalance,
  type Leaves,
} from '@talos/kyoko';
import type {
  GetContextMenuItemsParams,
  RowDoubleClickedEvent,
  SelectionChangedEvent,
  StateUpdatedEvent,
} from 'ag-grid-community';
import { compact, pick } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useFeatureFlag, useRoleAuth } from '../../../hooks';
import { useDisplaySettings } from '../../../providers/DisplaySettingsProvider';
import { useTradingSettings } from '../../../providers/TradingSettingsContext';
import { BalancesBlotterSummaryLine } from '../../Blotters/BalancesV2/SummaryLine/BalancesBlotterSummaryLine';
import { defaultCustomerBalancesColumns, defaultCustomerBalancesFilter } from './defaults';
import type { CustomerBalancesFilter, CustomerBalancesTabsProps } from './types';
import { useCustomerBalancesColumns } from './useCustomerBalancesColumns';
import {
  CUSTOMER_BALANCES_SPACE_PARAMETER_SUPPORT,
  colIDToFilterBuilderKey,
  useCustomerBalancesFilter,
} from './useCustomerBalancesFilter';

function getCustomerBalanceKey(cb: CustomerBalance) {
  return cb.rowID;
}

const AGG_SPECS = [{ valuePath: 'Equivalent.Amount', currencyPath: 'Equivalent.Currency' }] satisfies {
  valuePath: Leaves<CustomerBalance>;
  currencyPath: Leaves<CustomerBalance>;
}[];

type Groupings = 'Customer' | 'Asset' | typeof CUSTOM_BLOTTER_GROUPING;
const GROUPINGS: BlotterGroupingDefinition<Groupings, keyof CustomerBalance>[] = [
  {
    key: 'Customer',
    label: 'By Customer',
    rowGroupColumns: ['Counterparty'],
    mixpanelEvent: MixpanelEvent.GroupCustomer,
    buttonDataTestId: 'by-customer-button',
  },
  {
    key: 'Asset',
    label: 'By Asset',
    rowGroupColumns: ['Currency'],
    mixpanelEvent: MixpanelEvent.GroupAssets,
    buttonDataTestId: 'by-asset-button',
  },
  {
    key: CUSTOM_BLOTTER_GROUPING,
    label: 'Custom',
    rowGroupColumns: [],
    buttonDataTestId: 'custom-grouping-button',
  },
];

interface CustomerBalancesBlotterProps {
  blotterID: string;
  tab: CustomerBalancesTabsProps;
  onUpdateTab: (tab: CustomerBalancesTabsProps) => void;
  hideBlotterActions?: boolean;
  layout?: 'page' | 'panel';
  initialFiltersOpen?: boolean;
  onCloneTab: () => void;
  onRowDoubleClicked: (event: RowDoubleClickedEvent<CustomerBalance>) => void;
  onTransactionPrime: (customerBalance: CustomerBalance, transactionType: CustomerLedgerUpdateTypeEnum) => void;
  onNewBalanceTransaction: () => void;
}

export const CustomerBalancesBlotter = ({
  blotterID,
  tab,
  hideBlotterActions = false,
  layout = 'page',
  initialFiltersOpen,
  onCloneTab,
  onRowDoubleClicked,
  onTransactionPrime,
  onNewBalanceTransaction,
  onUpdateTab,
}: CustomerBalancesBlotterProps) => {
  const { requireCustomerBalancesCounterpartyFilter } = useFeatureFlag();
  const { isAuthorized } = useRoleAuth();
  const { showAllDecimalsInBlotters } = useTradingSettings();
  const mixpanel = useMixpanel();
  const [selectedRows, setSelectedRows] = useState<CustomerBalance[]>([]);
  const { homeCurrency } = useDisplaySettings();

  const defaultBlotterColumns = tab.defaultColumns ?? defaultCustomerBalancesColumns;
  const defaultFilter = tab.defaultFilter ?? defaultCustomerBalancesFilter;
  const showZeroBalances = tab.showZeroBalances ?? false;
  const showAllDecimals = tab.showAllDecimals ?? false;

  const defaultColumns = useCustomerBalancesColumns({
    defaultColumns: defaultBlotterColumns,
    onTransactionPrime,
    hideBlotterActions,
  });
  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
  });

  const filterResults = useCustomerBalancesFilter({
    persistedBlotterTable,
  });

  const { filterBuilderProps } = filterResults;

  const handleGridStateUpdated = useCallbackRef((event: StateUpdatedEvent) => {
    onGroupingStateUpdated(event);

    mixpanel.track(MixpanelEvent.ChangeBlotterColumns, {
      [MixpanelEventProperty.TabLabel]: 'Customer Transfers',
    });
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialFiltersOpen === true && filterResults.controlledKeys.length === 0 },
    filterBuilderProps: filterResults.filterBuilderProps,
  });
  const openClause = useSpaceParameterOpenClause(
    filterBuilderAccordion.openClause,
    CUSTOMER_BALANCES_SPACE_PARAMETER_SUPPORT
  );

  const rxjsFilterFunc = useCallback(
    (data: CustomerBalance) => {
      if (!showZeroBalances && data.Amount === '0') {
        return false;
      }

      return true;
    },
    [showZeroBalances]
  );
  const filterPipe = useWSFilterPipe({ getUniqueKey: getCustomerBalanceKey, filterFunc: rxjsFilterFunc });

  const { handleClickJson, jsonModal } = useJsonModal();

  const getCustomContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<CustomerBalance>) => {
      const data = params.node?.data;

      const items = compact([
        ...filterByCellValueMenuItem({
          params,
          openClause,
          filterableProperties: filterBuilderProps.properties,
          colIDToFilterBuilderKey,
          mixpanel,
        }),
        agGridMenuSeparator,
      ]);

      if (data != null && !hideBlotterActions && isAuthorized(ACTION.EDIT_CUSTOMER_BALANCE)) {
        items.unshift(agGridMenuSeparator);
        items.unshift({
          action: () => onTransactionPrime(data, CustomerLedgerUpdateTypeEnum.Withdraw),
          name: 'Create Withdrawal',
          icon: `<i class="ag-icon ${IconName.Upload}"/>`,
        });
        items.unshift({
          action: () => onTransactionPrime(data, CustomerLedgerUpdateTypeEnum.Deposit),
          name: 'Create Deposit',
          icon: `<i class="ag-icon ${IconName.Download}"/>`,
        });
      }

      return items;
    },
    [filterBuilderProps.properties, openClause, mixpanel, hideBlotterActions, isAuthorized, onTransactionPrime]
  );

  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        editable: false,
        hide: false,
        suppressColumnsToolPanel: false,
        params: {
          suppressCount: true,
        },
        sortable: true,
      }),
    []
  );

  const handleRowSelectionChanged = useCallback(({ api }: SelectionChangedEvent<CustomerBalance>) => {
    if (!isGridApiReady(api)) {
      return;
    }
    const rows = api.getSelectedRows();
    setSelectedRows(compact(rows));
  }, []);

  const aggPipe = useAggDeltaUpdatesPipe({
    getUniqueKey: getCustomerBalanceKey,
    aggSpecs: AGG_SPECS,
  });

  const baseRequest = useMemo(
    () => ({
      name: CUSTOMER_BALANCE,
      tag: blotterID,
      EquivalentCurrency: homeCurrency,
      ShowZeroBalances: true, // we always send ShowZeroBalances true to the backend, but we have an additional filtering on the frontend
    }),
    [blotterID, homeCurrency]
  );

  const request = useMemo(
    () => ({
      ...baseRequest,
      ...removeEmptyFilters(onlyServerKeys(filterResults.filter)),
    }),
    [baseRequest, filterResults.filter]
  );

  const hasRequiredFilterSelections = useMemo(() => {
    if (!requireCustomerBalancesCounterpartyFilter) {
      return true;
    }
    return filterResults.filter?.Counterparties != null && filterResults.filter.Counterparties.length > 0;
  }, [requireCustomerBalancesCounterpartyFilter, filterResults.filter]);

  const blotterTableSettings = useMemo(
    () => ({
      showAllDecimals: showAllDecimals ?? false,
    }),
    [showAllDecimals]
  );

  const blotterTable = useWsBlotterTable({
    request,
    allowRequest: hasRequiredFilterSelections,
    persistence: persistedBlotterTable,
    pipe: filterPipe,
    pinnedRowDataPipe: aggPipe,
    rowID: 'rowID' satisfies keyof CustomerBalance,
    columns: defaultColumns,
    handleClickJson,
    settings: blotterTableSettings,
    contextMenuOptions: { getCustomContextMenuItems },
    gridOptions: {
      onStateUpdated: handleGridStateUpdated,
      onSelectionChanged: handleRowSelectionChanged,
      onRowDoubleClicked,
      autoGroupColumnDef,
      rowGroupPanelShow: 'always',
      groupHideParentOfSingleChild: 'leafGroupsOnly',
      groupMaintainOrder: false,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
    },
    exportOptions: {
      name: 'Customer Balances',
      tabLabel: tab.label,
    },
  });

  const { expandAllGroups, collapseAllGroups, setRowGroupColumns } = blotterTable;

  const { onStateUpdated: onGroupingStateUpdated, groupingToggleButtons } = useBlotterGroupingToggling({
    blotterName: 'customer balances',
    setRowGroupColumns,
    groupings: GROUPINGS,
    allowNoGrouping: true,
  });

  const handleCloneTab = useCallbackRef(() => {
    onCloneTab();
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const updateShowZeroBalances = useCallbackRef((showZeroBalances: boolean) => {
    onUpdateTab({ ...tab, showZeroBalances });
  });

  const setShowAllDecimals = useCallbackRef((showAllDecimals: boolean) => {
    onUpdateTab({ ...tab, showAllDecimals });
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        accordionBodyPrefix={layout === 'panel' ? undefined : groupingToggleButtons}
        prefix={
          <>
            {layout === 'panel' ? groupingToggleButtons : null}
            <ExpandAllButton onClick={expandAllGroups} data-testid="button-expand-all" />
            <CollapseAllButton onClick={collapseAllGroups} data-testid="button-collapse-all" />
          </>
        }
        suffix={
          <>
            <BlotterTableExtrasMenu {...extrasMenuPopover}>
              {layout === 'panel' && (
                <>
                  <Button
                    startIcon={IconName.Plus}
                    size={FormControlSizes.Small}
                    disabled={!isAuthorized(ACTION.EDIT_CUSTOMER_BALANCE)}
                    data-testid="customer-balances-new-balance-transaction-button"
                    onClick={() => {
                      extrasMenuPopover.close();
                      onNewBalanceTransaction();
                    }}
                  >
                    New Balance Transaction
                  </Button>
                  <Divider />
                </>
              )}
              <Toggle
                size={FormControlSizes.Small}
                checked={showZeroBalances}
                onChange={updateShowZeroBalances}
                label="Show Zero Balances"
              />
              {!showAllDecimalsInBlotters && (
                <Toggle
                  size={FormControlSizes.Small}
                  checked={showAllDecimals}
                  onChange={setShowAllDecimals}
                  label="Show All Decimals"
                  data-testid="show-all-decimals-toggle"
                />
              )}
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>

              <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            </BlotterTableExtrasMenu>
          </>
        }
      />
      {/* hiding the blotter table instead of unmounting as it can get out of sync with the associated hooks otherwise, eventually instead we should have blotter table itself show the message much like it does when there are no rows. */}
      <BlotterTable {...blotterTable} hidden={!hasRequiredFilterSelections} />
      {!hasRequiredFilterSelections && (
        <BlotterTableOverlay>
          <span data-testid="select-counterparty-message">Please select a Counterparty</span>
        </BlotterTableOverlay>
      )}
      {hasRequiredFilterSelections && <BalancesBlotterSummaryLine rows={selectedRows} />}
      {jsonModal}
    </>
  );
};

function onlyServerKeys(filter: CustomerBalancesFilter | undefined): CustomerBalancesFilter {
  if (filter == null) {
    return {};
  }

  return pick(filter, ['Currencies', 'Counterparties']);
}
